import { Rocket, RefreshCw, ShieldCheck, Bitcoin, Lock, Crown, Trophy, Calendar } from "lucide-react";
import { Button } from "../../components/ui/button";

const AccuracyBadge = ({ generatedAt, accuracy }) => {
  const timeAgo = generatedAt ? (() => {
    const diff = Date.now() - new Date(generatedAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  })() : null;

  return (
    <div className="flex items-center gap-1.5 text-[9px]">
      <ShieldCheck className="w-3 h-3 text-emerald-500" />
      <span className="text-emerald-400 font-bold">{accuracy}% Accurate</span>
      {timeAgo && (
        <>
          <span className="text-zinc-600">|</span>
          <span className="text-zinc-500">Updated {timeAgo}</span>
        </>
      )}
    </div>
  );
};

export const CryptoPredictions = ({ cryptoPredictions, loadingCryptoPredictions, fetchCryptoPredictions, isPro, showUpgradePopup }) => {
  return (
    <section className="bg-gradient-to-r from-orange-500/5 via-red-500/5 to-orange-500/5 border border-orange-500/30 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500/30 to-red-500/30 rounded-full flex items-center justify-center">
            <Bitcoin className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              🚀 AI CRYPTO PICKS
              <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded animate-pulse">LIONAI™</span>
              {!isPro && <Lock className="w-4 h-4 text-zinc-500" />}
            </h2>
            <p className="text-xs text-zinc-500">High-volume cryptos • Super accurate AI predictions</p>
          </div>
        </div>
        {isPro && (
          <Button 
            onClick={fetchCryptoPredictions}
            disabled={loadingCryptoPredictions}
            className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 text-xs"
            size="sm"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loadingCryptoPredictions ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        )}
      </div>
      
      {!isPro ? (
        <div className="bg-black/40 border border-orange-500/20 p-8 text-center">
          <Bitcoin className="w-12 h-12 mx-auto text-orange-500/30 mb-3" />
          <p className="text-zinc-400 mb-4">Unlock AI Crypto Predictions</p>
          <Button 
            onClick={() => showUpgradePopup("AI Crypto Picks")}
            className="bg-gradient-to-r from-orange-500 to-red-500 text-white"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade to Pro
          </Button>
        </div>
      ) : loadingCryptoPredictions ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-black/50 border border-orange-500/20 p-5 animate-pulse">
              <div className="h-4 bg-zinc-800 rounded w-24 mb-3"></div>
              <div className="h-8 bg-zinc-800 rounded w-16 mb-2"></div>
              <div className="h-3 bg-zinc-800 rounded w-full"></div>
            </div>
          ))}
        </div>
      ) : cryptoPredictions?.predictions ? (
        <div className="space-y-6">
          {/* Daily Pick */}
          {cryptoPredictions.predictions.daily_pick && (
            <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-2 border-orange-500/40 p-5 rounded-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 px-3 py-1 bg-orange-500 text-black text-xs font-bold">
                DAILY PICK
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500/30 to-red-500/30 rounded-full flex items-center justify-center text-xl font-bold text-orange-400">
                    {cryptoPredictions.predictions.daily_pick.symbol?.substring(0, 2) || "?"}
                  </div>
                  <div>
                    <div className="text-xl font-bold text-white">{cryptoPredictions.predictions.daily_pick.symbol}</div>
                    <div className="text-sm text-zinc-400">{cryptoPredictions.predictions.daily_pick.name}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-lg font-bold ${
                    cryptoPredictions.predictions.daily_pick.action === 'BUY' ? 'text-emerald-400' :
                    cryptoPredictions.predictions.daily_pick.action === 'WATCH' ? 'text-amber-400' : 'text-blue-400'
                  }`}>
                    {cryptoPredictions.predictions.daily_pick.action}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {cryptoPredictions.predictions.daily_pick.confidence}% confidence
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-orange-500/20">
                <p className="text-sm text-zinc-300">{cryptoPredictions.predictions.daily_pick.reason}</p>
                <div className="flex items-center gap-4 mt-3">
                  <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded">
                    Target: {cryptoPredictions.predictions.daily_pick.target}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    cryptoPredictions.predictions.daily_pick.risk === 'LOW' ? 'bg-emerald-500/20 text-emerald-400' :
                    cryptoPredictions.predictions.daily_pick.risk === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                    'bg-amber-500/20 text-amber-400'
                  }`}>
                    {cryptoPredictions.predictions.daily_pick.risk} Risk
                  </span>
                  <span className="text-xs text-zinc-600">
                    {cryptoPredictions.predictions.daily_pick.timeframe}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Weekly & Monthly Picks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Weekly Picks */}
            {cryptoPredictions.predictions.weekly_picks && cryptoPredictions.predictions.weekly_picks.length > 0 && (
              <div className="bg-black/50 border border-blue-500/30 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-blue-400 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    WEEKLY PICKS
                  </span>
                  <span className="text-xs text-zinc-600">
                    Updates: {cryptoPredictions.next_updates?.weekly}
                  </span>
                </div>
                <div className="space-y-3">
                  {cryptoPredictions.predictions.weekly_picks.map((pick, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-blue-500/5 p-3 rounded">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center text-xs font-bold text-blue-400">
                          {pick.symbol?.substring(0, 2)}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-white">{pick.symbol}</div>
                          <div className="text-xs text-zinc-500">{pick.name}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-bold ${
                          pick.action === 'BUY' ? 'text-emerald-400' :
                          pick.action === 'WATCH' ? 'text-amber-400' : 'text-blue-400'
                        }`}>
                          {pick.action}
                        </div>
                        <div className="text-xs text-zinc-500">{pick.confidence}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Monthly Picks */}
            {cryptoPredictions.predictions.monthly_picks && cryptoPredictions.predictions.monthly_picks.length > 0 && (
              <div className="bg-black/50 border border-purple-500/30 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-purple-400 flex items-center gap-2">
                    <Trophy className="w-4 h-4" />
                    MONTHLY PICKS
                  </span>
                  <span className="text-xs text-zinc-600">
                    Updates: {cryptoPredictions.next_updates?.monthly}
                  </span>
                </div>
                <div className="space-y-3">
                  {cryptoPredictions.predictions.monthly_picks.map((pick, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-purple-500/5 p-3 rounded">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center text-xs font-bold text-purple-400">
                          {pick.symbol?.substring(0, 2)}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-white">{pick.symbol}</div>
                          <div className="text-xs text-zinc-500 max-w-[150px] truncate">{pick.name}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-bold ${
                          pick.action?.includes('STRONG') ? 'text-emerald-400' :
                          pick.action === 'BUY' ? 'text-emerald-400' : 'text-blue-400'
                        }`}>
                          {pick.action}
                        </div>
                        <div className="text-xs text-emerald-400">{pick.potential}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <p className="text-[9px] text-zinc-600 text-center">
            {cryptoPredictions.predictions.ai_generated ? '🤖 Generated by LIONAI using Gemini 3 Flash' : '📊 Rule-based analysis'} • 
            {cryptoPredictions.predictions.disclaimer || 'For educational purposes only. Not financial advice.'} • 
            Same predictions for all Pro users
          </p>
        </div>
      ) : (
        <div className="bg-black/40 border border-orange-500/20 p-6 text-center">
          <RefreshCw className="w-8 h-8 mx-auto text-orange-500/50 mb-2 animate-spin" />
          <p className="text-sm text-zinc-500">Loading AI predictions...</p>
        </div>
      )}
    </section>
  );
};
